// The source selector beside the alert row — "Bullhorn ▾" in every reference shot of the
// shortlist (`shortlist-extension-alerts.tpl.html:148`).
//
// ⚠️ IT RENDERS ONLY WHEN FOUR THINGS HOLD, and the caller owns all four:
//
//   currentViewMode !== FullMode
//   && shortlistSessionInfo.inATS
//   && !!externalSourceConfigurationResponse
//   && externalSourceConfigurationResponse.configurations.length > 0
//
// `inATS` is the one that cannot be inferred here: it means the extension is framed inside an
// ATS page rather than opened standalone, which is a property of the SESSION, not of this
// component. A tenant with sources configured still shows no selector when the user opened the
// frame outside their ATS. Rendering it anyway would put a control in front of users who have
// nothing to switch between.
import type { ReactElement } from 'react'
import { currentSourceOption, type SocialHost, type SourceOption } from './sourceOptions.ts'
import { uibDropdown } from '../common/uibDropdown.ts'
import { asset } from '../assetBase.ts'

export interface SourcesDropdownProps {
  options: readonly SourceOption[]
  /** `ctrl.currentExternalSource` — undefined or null means the tenant's own ATS. */
  current?: SocialHost | null
  open?: boolean
  onToggle?: () => void
  onSelect?: (option: SourceOption) => void
}

export function SourcesDropdown({ options, current, open = false, onToggle, onSelect }: SourcesDropdownProps): ReactElement {
  const selected = currentSourceOption(options, current)

  return (
    <div className="whitelabel-sources-dropdown-container">
      {/* `uib-dropdown` sits on THIS span upstream (`shortlist-extension-alerts.tpl.html:155`),
          and the `open` it adds is the only thing that makes the menu below visible — see
          `uibDropdown`. Without it the list rendered, correctly, at `display: none`.

          ⚠️ AND `position: relative` ON THE SAME ELEMENT, or it opens off the bottom of the
          frame. `.dropdown-menu` is `top: 100%` of its POSITIONED ancestor, and nothing in
          this subtree is positioned — so the menu resolved against something near the document
          root and landed at y=762 in a 760px frame: open, `display: block`, and below the fold.
          Indistinguishable from "the dropdown does nothing", which is how it was reported.
          This span is `width: 100%` in the stylesheet, so anchoring here puts the 180px list
          under the source row where upstream draws it. */}
      <span
        className={uibDropdown('whitelabel-sources-dropdown-content-container', open)}
        style={{ position: 'relative' }}
      >
        <span
          className="cursor-pointer hover-lighter whitelabel-sources-dropdown-current-source"
          role="button"
          aria-label="Change source"
          tabIndex={0}
          onClick={onToggle}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            onToggle?.()
          }}
        >
          {/* `cdn-src` upstream — a directive that prefixes the CDN host. Here `/static` is
              proxied onto this origin (proxy-paths.ts), so the path resolves as written. */}
          {selected?.icon === undefined ? null : (
            <img className="whitelabel-sources-source-icon" src={asset(selected.icon)} alt="" />
          )}
          <span>{selected?.name}</span>
          <i className="fas fa-caret-down" />
        </span>
        {open ? (
          <ul className="dropdown-menu">
            {options.map((option) => (
              <li key={option.type ?? 'ats'}>
                <a
                  href=""
                  className="whitelabel-sources-dropdown-source"
                  onClick={(event) => {
                    event.preventDefault()
                    onSelect?.(option)
                  }}
                >
                  {option.icon === undefined ? null : (
                    <img className="whitelabel-sources-source-icon" src={asset(option.icon)} alt="" />
                  )}
                  <span
                    className={`whitelabel-source-option-name${
                      option.type === (current ?? null) ? ' whitelabel-source-option-selected' : ''
                    }`}
                  >
                    {option.name}
                  </span>
                  {/* The tick is on the SAME comparison as the bold class, so the two can
                      never disagree — upstream writes the expression twice. */}
                  {option.type === (current ?? null) ? <i className="fas fa-check" /> : null}
                </a>
              </li>
            ))}
          </ul>
        ) : null}
      </span>
    </div>
  )
}
